/**
 * SelectionManager - Handles selection state and Konva.Transformer
 * Implements single selection, multi-selection, and marquee selection
 */

import Konva from 'konva';
import { EventEmitter } from '../core/EventEmitter';
import { Point, BoundingBox } from '../core/types';


export interface SelectionState {
  selectedIds: string[];
  isMultiSelect: boolean;
}

export interface MarqueeState {
  isActive: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export interface SelectionManagerOptions {
  enableRotation?: boolean;
  enableResize?: boolean;
  keepAspectRatio?: boolean;
  rotationSnaps?: number[];
  rotationSnapTolerance?: number;
}

const DEFAULT_OPTIONS: Required<SelectionManagerOptions> = {
  enableRotation: true,
  enableResize: true,
  keepAspectRatio: false,
  rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
  rotationSnapTolerance: 5,
};

export class SelectionManager {
  private layer: Konva.Layer;
  private emitter: EventEmitter;
  private options: Required<SelectionManagerOptions>;
  
  // Selection state
  private selectedIds: string[] = [];
  
  // Transformer
  private transformer: Konva.Transformer;
  
  // Marquee selection
  private marqueeRect: Konva.Rect | null = null;
  private marqueeState: MarqueeState = {
    isActive: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  };
  
  // Corner rotation state
  private isRotating: boolean = false;
  private rotationStartAngle: number = 0;
  private rotationStartPointerAngle: number = 0;
  private rotatingNode: Konva.Node | null = null;
  private rotationTriggers: Konva.Circle[] = [];
  private rotationZoneSize: number = 12; // Size of rotation trigger zone
  private rotationZoneOffset: number = 5; // Distance from corner
  private debugRotationTriggers: boolean = true; // Set to true to see rotation zones (DEBUG: enabled)
  private rotationAnimationFrame: number | null = null;
  private rotationTriggerIndex: number = -1; // Track which corner trigger was used
  
  // Callbacks
  private onTransformStart: (() => void) | null = null;
  private onTransformEnd: ((id: string, newAttrs: any, oldAttrs?: any) => void) | null = null;

  constructor(
    layer: Konva.Layer,
    emitter: EventEmitter,
    options: SelectionManagerOptions = {}
  ) {
    this.layer = layer;
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
    
    // Create transformer
    this.transformer = this.createTransformer();
    this.layer.add(this.transformer);
    
    // Create marquee rect (initially hidden)
    this.marqueeRect = this.createMarqueeRect();
    this.layer.add(this.marqueeRect);
    
    // Create rotation triggers (invisible circles at corners)
    this.createRotationTriggers();
  }
  

  /**
   * Create invisible rotation trigger zones at corners
   */
  private createRotationTriggers(): void {
    const corners = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];
    
    corners.forEach((corner) => {
      
      const trigger = new Konva.Circle({
        radius: this.rotationZoneSize,
        // Use a slight fill for hit detection (Konva ignores truly transparent shapes for events)
        fill: this.debugRotationTriggers ? 'rgba(255, 0, 0, 0.0)' : 'rgba(0, 0, 0, 0.0)',
        stroke: 'transparent',
        visible: false,
        listening: true,
        name: `rotation-trigger-${corner}`,
        hitStrokeWidth: 0,
      });
      
      // Rotation cursor on hover (corner-specific direction)
      trigger.on('mouseenter', () => {

        const stage = this.layer.getStage();
        if (stage) {
          stage.container().style.cursor = 'crosshair';
        }
      });
      
      trigger.on('mouseleave', () => {

        if (!this.isRotating) {
          const stage = this.layer.getStage();
          if (stage) {
            stage.container().style.cursor = 'default';
          }
        }
      });
      
      // Start rotation on mousedown - cursor changes to active rotation
      trigger.on('mousedown', (e) => {

        e.cancelBubble = true;
        const stage = this.layer.getStage();
        if (stage) {
          stage.container().style.cursor = 'crosshair';
        }
        // Store which corner trigger this is (0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left)
        const cornerIndex = corners.indexOf(corner);
        this.rotationTriggerIndex = cornerIndex;
        this.startCornerRotation(e);
      });
      
      this.rotationTriggers.push(trigger);
      this.layer.add(trigger);
    });
  }
  
  /**
   * Start rotation from corner
   */
  private startCornerRotation(e: Konva.KonvaEventObject<MouseEvent>): void {
    const nodes = this.transformer.nodes();
    if (nodes.length !== 1) return;
    
    const node = nodes[0];
    const stage = this.layer.getStage();
    if (!stage) return;
    stage.container().style.cursor = 'crosshair';
    
    this.isRotating = true;
    this.rotatingNode = node;
    this.rotationStartAngle = node.rotation();
    
    // Cast to Shape for attribute access
    const shapeNode = node as Konva.Shape;
    
    // Get element dimensions
    const elementWidth = (shapeNode.getAttr('elementWidth') as number | undefined) || shapeNode.width();
    const elementHeight = (shapeNode.getAttr('elementHeight') as number | undefined) || shapeNode.height();
    
    // Calculate center of the node in local coordinates
    const centerX = elementWidth / 2;
    const centerY = elementHeight / 2;
    
    // Use Konva's native offset feature for rotation around center
    // Store original offset and position
    const originalOffsetX = node.offsetX();
    const originalOffsetY = node.offsetY();
    const originalX = node.x();
    const originalY = node.y();
    
    // Get the absolute transform BEFORE changing offset to find where center is
    const absTransform = node.getAbsoluteTransform();
    const centerAbsPoint = absTransform.point({ x: centerX, y: centerY });
    
    // Store the absolute center position for later use
    shapeNode.setAttr('rotationCenterAbsX', centerAbsPoint.x);
    shapeNode.setAttr('rotationCenterAbsY', centerAbsPoint.y);
    
    // Set offset to center so rotation happens around center (like Konva's native rotation)
    node.offsetX(centerX);
    node.offsetY(centerY);
    
    // With offset at center, we need position so that center maps to centerAbsPoint
    // Use iterative refinement in absolute space, then convert final result to layer space
    const layer = node.getLayer();
    if (layer) {
      const layerTransform = layer.getAbsoluteTransform();
      const inverseLayerTransform = layerTransform.copy().invert();
      
      // Start with approximate position - convert absolute center to layer coordinates
      let targetPosInLayer = inverseLayerTransform.point(centerAbsPoint);
      node.position(targetPosInLayer);
      
      // Iteratively refine until the center maps correctly
      for (let i = 0; i < 5; i++) {
        const currentAbsTransform = node.getAbsoluteTransform();
        const currentCenterAbs = currentAbsTransform.point({ x: centerX, y: centerY });
        const errorX = centerAbsPoint.x - currentCenterAbs.x;
        const errorY = centerAbsPoint.y - currentCenterAbs.y;
        
        // If error is very small, we're done
        if (Math.abs(errorX) < 0.001 && Math.abs(errorY) < 0.001) {
          break;
        }
        
        // Convert the error vector to layer space and adjust position
        const errorTargetAbs = { x: currentCenterAbs.x + errorX, y: currentCenterAbs.y + errorY };
        const errorTargetInLayer = inverseLayerTransform.point(errorTargetAbs);
        const currentCenterInLayer = inverseLayerTransform.point(currentCenterAbs);
        const correctionX = errorTargetInLayer.x - currentCenterInLayer.x;
        const correctionY = errorTargetInLayer.y - currentCenterInLayer.y;
        
        node.position({
          x: node.x() + correctionX,
          y: node.y() + correctionY
        });
      }
    }
    
    // Get pointer position for initial angle calculation
    const pointerPos = stage.getPointerPosition();
    if (pointerPos) {
      // Calculate initial angle from center to pointer
      this.rotationStartPointerAngle = Math.atan2(
        pointerPos.y - centerAbsPoint.y,
        pointerPos.x - centerAbsPoint.x
      ) * 180 / Math.PI;
    }
    
    // Store original values for restoration
    shapeNode.setAttr('rotationOriginalOffsetX', originalOffsetX);
    shapeNode.setAttr('rotationOriginalOffsetY', originalOffsetY);
    shapeNode.setAttr('rotationOriginalX', originalX);
    shapeNode.setAttr('rotationOriginalY', originalY);
    shapeNode.setAttr('rotationCenterLocalX', centerX);
    shapeNode.setAttr('rotationCenterLocalY', centerY);
    
    // Notify transform started
    this.onTransformStart?.();
    
    // Store start values for undo
    this.transformStartValues.set(node.id(), {
      x: originalX,
      y: originalY,
      width: elementWidth,
      height: elementHeight,
      rotation: node.rotation(),
    });
    
    // Setup global mouse move and up handlers with throttling via requestAnimationFrame
    let pendingMouseMove: MouseEvent | null = null;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      pendingMouseMove = moveEvent;
      if (!this.rotationAnimationFrame) {
        this.rotationAnimationFrame = requestAnimationFrame(() => {
          if (pendingMouseMove) {
            this.handleRotationMove(pendingMouseMove);
            pendingMouseMove = null;
          }
          this.rotationAnimationFrame = null;
        });
      }
    };
    
    const handleMouseUp = () => {
      // Cancel any pending animation frame
      if (this.rotationAnimationFrame) {
        cancelAnimationFrame(this.rotationAnimationFrame);
        this.rotationAnimationFrame = null;
      }
      // Process any pending move event before ending
      if (pendingMouseMove) {
        this.handleRotationMove(pendingMouseMove);
        pendingMouseMove = null;
      }
      this.endCornerRotation();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }
  
  /**
   * Handle rotation during mouse move (using Konva's native offset-based rotation)
   */
  private handleRotationMove(e: MouseEvent): void {
    if (!this.isRotating || !this.rotatingNode) return;
    
    const node = this.rotatingNode;
    const stage = this.layer.getStage();
    if (!stage) return;
    
    // Use stored center absolute position (center should stay fixed during rotation)
    const shapeNode = node as Konva.Shape;
    const centerAbsX = (shapeNode.getAttr('rotationCenterAbsX') as number | undefined) ?? 0;
    const centerAbsY = (shapeNode.getAttr('rotationCenterAbsY') as number | undefined) ?? 0;

    // Get current pointer position relative to stage
    const stageBox = stage.container().getBoundingClientRect();
    const pointerX = e.clientX - stageBox.left;
    const pointerY = e.clientY - stageBox.top;
    
    // Calculate current angle from center to pointer
    const currentPointerAngle = Math.atan2(
      pointerY - centerAbsY,
      pointerX - centerAbsX
    ) * 180 / Math.PI;
    
    // Calculate rotation delta, handling angle wrapping (-180 to 180 boundary)
    let angleDelta = currentPointerAngle - this.rotationStartPointerAngle;
    
    // Normalize angle delta to -180 to 180 range
    if (angleDelta > 180) {
      angleDelta -= 360;
    } else if (angleDelta < -180) {
      angleDelta += 360;
    }
    
    // Apply rotation delta directly (1:1 rotation - natural Konva behavior)
    let newRotation = this.rotationStartAngle + angleDelta;
    
    // Snap to 15-degree increments if shift is held
    if (e.shiftKey) {
      newRotation = Math.round(newRotation / 15) * 15;
    }
    
    // Apply rotation - Konva handles position adjustment automatically with offset
    node.rotation(newRotation);
    
    // Don't update rotation triggers position during drag (expensive) - update at end
    // this.updateRotationTriggersPosition();
    
    this.layer.batchDraw();
  }
  
  /**
   * End rotation
   */
  private endCornerRotation(): void {
    if (!this.isRotating || !this.rotatingNode) return;
    
    const node = this.rotatingNode;
    const id = node.id();
    
    // Ensure the node ID is still in selectedIds (preserve selection)
    if (!this.selectedIds.includes(id)) {
      this.selectedIds = [id];
    }
    
    // Reset cursor
    const stage = this.layer.getStage();
    if (stage) {
      stage.container().style.cursor = 'default';
    }
    
    // Cast to Shape for attribute access
    const shapeNode = node as Konva.Shape;
    
    // Get current center position in absolute coordinates (while offset is still at center)
    const centerLocalX = (shapeNode.getAttr('rotationCenterLocalX') as number | undefined) ?? 0;
    const centerLocalY = (shapeNode.getAttr('rotationCenterLocalY') as number | undefined) ?? 0;
    const absTransform = node.getAbsoluteTransform();
    const centerAbsPoint = absTransform.point({ x: centerLocalX, y: centerLocalY });
    
    // Restore original offset
    const originalOffsetX = (shapeNode.getAttr('rotationOriginalOffsetX') as number | undefined) ?? 0;
    const originalOffsetY = (shapeNode.getAttr('rotationOriginalOffsetY') as number | undefined) ?? 0;
    node.offsetX(originalOffsetX);
    node.offsetY(originalOffsetY);
    
    // Now we need to set position so center maps back to centerAbsPoint with restored offset
    // Use iterative refinement in absolute space, then convert final result to layer space
    const layer = node.getLayer();
    if (layer) {
      const layerTransform = layer.getAbsoluteTransform();
      const inverseLayerTransform = layerTransform.copy().invert();
      
      // Start with approximate position - convert absolute center to layer coordinates
      let targetPosInLayer = inverseLayerTransform.point(centerAbsPoint);
      node.position(targetPosInLayer);
      
      // Iteratively refine until the center maps correctly
      for (let i = 0; i < 5; i++) {
        const currentAbsTransform = node.getAbsoluteTransform();
        const currentCenterAbs = currentAbsTransform.point({ 
          x: centerLocalX ?? 0, 
          y: centerLocalY ?? 0 
        });
        const errorX = centerAbsPoint.x - currentCenterAbs.x;
        const errorY = centerAbsPoint.y - currentCenterAbs.y;
        
        // If error is very small, we're done
        if (Math.abs(errorX) < 0.001 && Math.abs(errorY) < 0.001) {
          break;
        }
        
        // Convert the error vector to layer space and adjust position
        const errorTargetAbs = { x: currentCenterAbs.x + errorX, y: currentCenterAbs.y + errorY };
        const errorTargetInLayer = inverseLayerTransform.point(errorTargetAbs);
        const currentCenterInLayer = inverseLayerTransform.point(currentCenterAbs);
        const correctionX = errorTargetInLayer.x - currentCenterInLayer.x;
        const correctionY = errorTargetInLayer.y - currentCenterInLayer.y;
        
        node.position({
          x: node.x() + correctionX,
          y: node.y() + correctionY
        });
      }
    }
    
    // Get element dimensions
    const elementWidth = (shapeNode.getAttr('elementWidth') as number | undefined) || shapeNode.width();
    const elementHeight = (shapeNode.getAttr('elementHeight') as number | undefined) || shapeNode.height();
    
    const newAttrs = {
      x: node.x(),
      y: node.y(),
      width: elementWidth,
      height: elementHeight,
      rotation: node.rotation(),
      scaleX: 1,
      scaleY: 1,
    };
    
    // Get old values for undo
    const oldAttrs = this.transformStartValues.get(id);
    
    // Notify transform ended
    if (this.onTransformEnd) {
      this.onTransformEnd(id, newAttrs, oldAttrs);
    }
    
    this.transformStartValues.delete(id);
    
    // Reset rotation state
    this.isRotating = false;
    this.rotatingNode = null;
    
    // Clean up stored rotation attributes
    shapeNode.setAttr('rotationOriginalOffsetX', undefined);
    shapeNode.setAttr('rotationOriginalOffsetY', undefined);
    shapeNode.setAttr('rotationOriginalX', undefined);
    shapeNode.setAttr('rotationOriginalY', undefined);
    shapeNode.setAttr('rotationCenterLocalX', undefined);
    shapeNode.setAttr('rotationCenterLocalY', undefined);
    shapeNode.setAttr('rotationCenterAbsX', undefined);
    shapeNode.setAttr('rotationCenterAbsY', undefined);
    
    // Reset rotation trigger index
    this.rotationTriggerIndex = -1;
    
    // Ensure the node is still attached to the transformer (it should be, but verify)
    const currentNodes = this.transformer.nodes();
    if (currentNodes.length === 0 || !currentNodes.includes(node)) {
      // If somehow the node was removed, reattach it
      this.transformer.nodes([node]);
    }
    
    // Force transformer update and update rotation triggers position
    this.transformer.forceUpdate();
    this.updateRotationTriggersPosition();
    this.layer.batchDraw();
  }
  
  /**
   * Update rotation trigger positions based on transformer bounds
   */
  private updateRotationTriggersPosition(): void {
    const nodes = this.transformer.nodes();
    if (nodes.length !== 1) {
      // Hide triggers if not exactly one node selected
      this.rotationTriggers.forEach(t => t.visible(false));
      return;
    }
    
    const node = nodes[0];
    const stage = this.layer.getStage();
    if (!stage) return;
    
    // Get node position and dimensions in layer coordinates
    const nodeX = node.x();
    const nodeY = node.y();
    const elementWidth = node.getAttr('elementWidth') || node.width();
    const elementHeight = node.getAttr('elementHeight') || node.height();
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const rotation = node.rotation();
    
    // Calculate actual dimensions
    const actualWidth = elementWidth * scaleX;
    const actualHeight = elementHeight * scaleY;
    
    // Get the 4 corners of the bounding box in local coordinates
    // We need to account for rotation
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    
    // Helper to rotate a point around the node's origin
    const rotatePoint = (x: number, y: number) => ({
      x: nodeX + x * cos - y * sin,
      y: nodeY + x * sin + y * cos,
    });
    
    // Corner offsets relative to node origin (before rotation)
    const cornerOffsets = [
      { x: 0, y: 0 }, // top-left
      { x: actualWidth, y: 0 }, // top-right
      { x: actualWidth, y: actualHeight }, // bottom-right
      { x: 0, y: actualHeight }, // bottom-left
    ];
    
    // Calculate rotated corner positions
    const rotatedCorners = cornerOffsets.map(c => rotatePoint(c.x, c.y));
    
    // Position triggers outside each corner
    const offset = this.rotationZoneOffset + this.rotationZoneSize;
    
    // Calculate offset directions based on rotation for each corner
    const triggerPositions = [
      // top-left: offset towards -x, -y (rotated)
      { 
        x: rotatedCorners[0].x + (-offset * cos - (-offset) * sin),
        y: rotatedCorners[0].y + (-offset * sin + (-offset) * cos)
      },
      // top-right: offset towards +x, -y (rotated)
      { 
        x: rotatedCorners[1].x + (offset * cos - (-offset) * sin),
        y: rotatedCorners[1].y + (offset * sin + (-offset) * cos)
      },
      // bottom-right: offset towards +x, +y (rotated)
      { 
        x: rotatedCorners[2].x + (offset * cos - offset * sin),
        y: rotatedCorners[2].y + (offset * sin + offset * cos)
      },
      // bottom-left: offset towards -x, +y (rotated)
      { 
        x: rotatedCorners[3].x + (-offset * cos - offset * sin),
        y: rotatedCorners[3].y + (-offset * sin + offset * cos)
      },
    ];
    
    triggerPositions.forEach((pos, index) => {
      const trigger = this.rotationTriggers[index];
      if (trigger) {
        trigger.position(pos);
        trigger.visible(true);
        trigger.moveToTop();
      }
    });
    
    this.layer.batchDraw();
  }
  
  /**
   * Hide rotation triggers
   */
  private hideRotationTriggers(): void {
    this.rotationTriggers.forEach(t => t.visible(false));
  }

  /**
   * Create the Konva Transformer with styling matching current implementation
   */
  private createTransformer(): Konva.Transformer {
    return new Konva.Transformer({
      rotateEnabled: false, // Disable rotation handle - rotation handled separately if needed
      resizeEnabled: this.options.enableResize,
      keepRatio: this.options.keepAspectRatio,

      // Styling matching current implementation
      borderStroke: '#0066FF',
      borderStrokeWidth: 2,
      anchorStroke: '#0066FF',
      anchorFill: '#FFFFFF',
      anchorSize: 10,
      anchorCornerRadius: 2,

      // Behavior
      centeredScaling: false,
      ignoreStroke: true,
      flipEnabled: false,

      // Minimum size constraint
      boundBoxFunc: (oldBox, newBox) => {
        if (Math.abs(newBox.width) < 20 || Math.abs(newBox.height) < 20) {
          return oldBox;
        }
        return newBox;
      },

      // Enabled anchors - only 4 corner anchors to avoid conflict with anchor points
      enabledAnchors: this.options.enableResize
        ? [
            'top-left',
            'top-right',
            'bottom-right',
            'bottom-left',
          ]
        : [],
    });
  }

  /**
   * Create the marquee selection rectangle
   */
  private createMarqueeRect(): Konva.Rect {
    return new Konva.Rect({
      fill: 'rgba(0, 102, 255, 0.15)',
      stroke: '#0066FF',
      strokeWidth: 2,
      dash: [4, 4],
      visible: false,
      listening: false,
      perfectDrawEnabled: false,
      shadowForStrokeEnabled: false,
      hitStrokeWidth: 0,
    });
  }

  // Store transform start values for undo
  private transformStartValues: Map<string, { x: number; y: number; width: number; height: number; rotation: number }> = new Map();

  /**
   * Setup transform handlers (resize only - rotation handled by rotation triggers)
   */
  setupTransformHandler(): void {
    // Capture values at transform start for undo
    this.transformer.on('transformstart', (e) => {
      const node = e.target;
      const id = node.id();
      if (!id) return;
      
      // Use custom attributes for actual element dimensions (not affected by shadows)
      const shapeNode = node as Konva.Shape;
      const elementWidth = (shapeNode.getAttr('elementWidth') as number | undefined) || shapeNode.width();
      const elementHeight = (shapeNode.getAttr('elementHeight') as number | undefined) || shapeNode.height();
      
      this.transformStartValues.set(id, {
        x: node.x(),
        y: node.y(),
        width: elementWidth,
        height: elementHeight,
        rotation: node.rotation(),
      });
      
      // Hide rotation triggers during resize
      this.hideRotationTriggers();
      
      // Notify that transform has started
      this.onTransformStart?.();
    });

    this.transformer.on('transformend', (e) => {
      const node = e.target;
      const id = node.id();

      if (!id || !this.onTransformEnd) return;

      // Use custom attributes for actual element dimensions (not affected by shadows)
      const shape = node as Konva.Shape;
      const elementWidth = (shape.getAttr('elementWidth') as number | undefined) || shape.width();
      const elementHeight = (shape.getAttr('elementHeight') as number | undefined) || shape.height();
      
      // Normal resize - apply scale
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const newWidth = Math.max(20, elementWidth * Math.abs(scaleX));
      const newHeight = Math.max(20, elementHeight * Math.abs(scaleY));

      const nodePosition = node.position();
      
      const newAttrs = {
        x: nodePosition.x,
        y: nodePosition.y,
        width: newWidth,
        height: newHeight,
        rotation: node.rotation(),
        // Include scale reset info
        scaleX: 1,
        scaleY: 1,
      };
      
      // Reset the node's scale to 1 (we're using width/height instead)
      node.scaleX(1);
      node.scaleY(1);
      
      // Update custom attributes with new dimensions
      shape.setAttr('elementWidth', newWidth);
      shape.setAttr('elementHeight', newHeight);
      
      // Get old values for undo
      const oldAttrs = this.transformStartValues.get(id);

      // Call callback first so renderer updates the element size
      this.onTransformEnd(id, newAttrs, oldAttrs);
      this.transformStartValues.delete(id);

      // Force transformer to recalculate its bounds after element is resized
      this.transformer.forceUpdate();
      
      // Update rotation triggers position
      this.updateRotationTriggersPosition();
      
      this.layer.batchDraw();
    });
  }

  /**
   * Set transform start callback
   */
  setOnTransformStart(callback: () => void): void {
    this.onTransformStart = callback;
  }

  /**
   * Set transform end callback
   */
  setOnTransformEnd(callback: (id: string, newAttrs: any, oldAttrs?: any) => void): void {
    this.onTransformEnd = callback;
    this.setupTransformHandler();
  }

  /**
   * Select a single element
   */
  selectElement(id: string, multiSelect: boolean = false): void {
    if (multiSelect) {
      // Toggle selection in multi-select mode
      const index = this.selectedIds.indexOf(id);
      if (index >= 0) {
        this.selectedIds.splice(index, 1);
      } else {
        this.selectedIds.push(id);
      }
    } else {
      // Single select - replace selection
      this.selectedIds = [id];
    }
    
    this.updateTransformer();
    this.emitSelectionChange();
  }

  /**
   * Select multiple elements at once
   * @param ids - Element IDs to select
   * @param replace - Replace existing selection (default true)
   * @param emit - Emit selection change event (default true, set false when syncing from React)
   */
  selectElements(ids: string[], replace: boolean = true, emit: boolean = true): void {
    if (replace) {
      this.selectedIds = [...ids];
    } else {
      // Add to existing selection, avoiding duplicates
      ids.forEach((id) => {
        if (!this.selectedIds.includes(id)) {
          this.selectedIds.push(id);
        }
      });
    }
    
    this.updateTransformer();
    if (emit) {
      this.emitSelectionChange();
    }
  }

  /**
   * Deselect a single element
   */
  deselectElement(id: string): void {
    const index = this.selectedIds.indexOf(id);
    if (index >= 0) {
      this.selectedIds.splice(index, 1);
      this.updateTransformer();
      this.emitSelectionChange();
    }
  }

  /**
   * Clear all selection
   */
  clearSelection(): void {
    if (this.selectedIds.length > 0) {
      this.selectedIds = [];
      this.transformer.nodes([]);
      this.hideRotationTriggers();
      this.layer.batchDraw();
      this.emitSelectionChange();
    }
  }

  /**
   * Check if an element is selected
   */
  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  /**
   * Get selected IDs
   */
  getSelectedIds(): string[] {
    return [...this.selectedIds];
  }

  /**
   * Update transformer nodes based on selection
   */
  private updateTransformer(): void {
    const selectedNodes: Konva.Node[] = [];
    
    this.selectedIds.forEach((id) => {
      const node = this.layer.findOne(`#${id}`);
      if (node) {
        selectedNodes.push(node);
      }
    });
    
    if (selectedNodes.length > 0) {
      this.transformer.nodes(selectedNodes);
      
      // Ensure transformer is in the correct layer
      if (!this.transformer.getParent() || this.transformer.getParent() !== this.layer) {
        if (this.transformer.getParent()) {
          this.transformer.remove();
        }
        this.layer.add(this.transformer);
      }
      
      // Move transformer to top
      this.transformer.moveToTop();
      
      // Update rotation triggers position (only for single selection)

      setTimeout(() => {

        this.updateRotationTriggersPosition();
      }, 0);
    } else {
      this.transformer.nodes([]);
      // Hide rotation triggers when nothing selected
      this.hideRotationTriggers();
    }
    
    this.layer.batchDraw();
  }

  /**
   * Emit selection change event
   */
  private emitSelectionChange(): void {
    this.emitter.emit('selectionChange', {
      selectedIds: [...this.selectedIds],
    });
  }

  // ============================================================================
  // MARQUEE SELECTION
  // ============================================================================

  /**
   * Start marquee selection
   */
  startMarquee(x: number, y: number): void {
    this.marqueeState = {
      isActive: true,
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
    };
    
    if (this.marqueeRect) {
      this.marqueeRect.visible(true);
      this.marqueeRect.x(x);
      this.marqueeRect.y(y);
      this.marqueeRect.width(0);
      this.marqueeRect.height(0);
      // Move marquee to top so it's visible above all elements
      this.marqueeRect.moveToTop();
    }
    
    this.layer.batchDraw();
  }

  /**
   * Update marquee selection
   */
  updateMarquee(x: number, y: number): void {
    if (!this.marqueeState.isActive) return;
    
    this.marqueeState.currentX = x;
    this.marqueeState.currentY = y;
    
    // Calculate normalized rectangle
    const minX = Math.min(this.marqueeState.startX, x);
    const minY = Math.min(this.marqueeState.startY, y);
    const width = Math.abs(x - this.marqueeState.startX);
    const height = Math.abs(y - this.marqueeState.startY);
    
    if (this.marqueeRect) {
      this.marqueeRect.x(minX);
      this.marqueeRect.y(minY);
      this.marqueeRect.width(width);
      this.marqueeRect.height(height);
      // Ensure marquee stays on top during updates
      this.marqueeRect.moveToTop();
    }
    
    this.layer.batchDraw();
  }

  /**
   * End marquee selection and select elements within bounds
   */
  endMarquee(addToSelection: boolean = false): string[] {
    if (!this.marqueeState.isActive) return [];
    
    // Calculate selection bounds
    const bounds: BoundingBox = {
      x: Math.min(this.marqueeState.startX, this.marqueeState.currentX),
      y: Math.min(this.marqueeState.startY, this.marqueeState.currentY),
      width: Math.abs(this.marqueeState.currentX - this.marqueeState.startX),
      height: Math.abs(this.marqueeState.currentY - this.marqueeState.startY),
    };
    
    // Reset marquee state
    this.marqueeState.isActive = false;
    if (this.marqueeRect) {
      this.marqueeRect.visible(false);
    }
    
    // Only select if marquee is large enough
    if (bounds.width < 5 || bounds.height < 5) {
      this.layer.batchDraw();
      return [];
    }
    
    // Find elements in bounds
    const elementsInBounds = this.findElementsInBounds(bounds);
    
    if (elementsInBounds.length > 0) {
      if (addToSelection) {
        this.selectElements(elementsInBounds, false);
      } else {
        this.selectElements(elementsInBounds, true);
      }
    }
    
    this.layer.batchDraw();
    return elementsInBounds;
  }

  /**
   * Cancel marquee selection
   */
  cancelMarquee(): void {
    this.marqueeState.isActive = false;
    if (this.marqueeRect) {
      this.marqueeRect.visible(false);
    }
    this.layer.batchDraw();
  }

  /**
   * Check if marquee selection is active
   */
  isMarqueeActive(): boolean {
    return this.marqueeState.isActive;
  }

  /**
   * Find elements within bounds
   */
  private findElementsInBounds(bounds: BoundingBox): string[] {
    const elementsInBounds: string[] = [];
    
    // Get all nodes in layer
    this.layer.getChildren().forEach((node) => {
      const id = node.id();
      
      // Skip non-element nodes (background, transformer, marquee)
      if (!id || id === '' || node === this.transformer || node === this.marqueeRect) {
        return;
      }
      
      // Skip if it's a connector
      if (node.hasName('connector')) {
        return;
      }
      
      // Get node bounds in layer coordinates
      // Use getClientRect with relativeTo: this.layer to get coordinates relative to the layer
      const nodeRect = node.getClientRect({ relativeTo: this.layer });
      
      // Check intersection using layer coordinates
      const intersects = this.boundsIntersect(bounds, {
        x: nodeRect.x,
        y: nodeRect.y,
        width: nodeRect.width,
        height: nodeRect.height,
      });
      
      if (intersects) {
        elementsInBounds.push(id);
      }
    });
    
    return elementsInBounds;
  }

  /**
   * Check if two bounding boxes intersect
   */
  private boundsIntersect(a: BoundingBox, b: BoundingBox): boolean {
    return !(
      a.x + a.width < b.x ||
      b.x + b.width < a.x ||
      a.y + a.height < b.y ||
      b.y + b.height < a.y
    );
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Enable or disable rotation
   */
  setRotationEnabled(enabled: boolean): void {
    this.options.enableRotation = enabled;
    this.transformer.rotateEnabled(enabled);
  }

  /**
   * Enable or disable resize
   */
  setResizeEnabled(enabled: boolean): void {
    this.options.enableResize = enabled;
    this.transformer.resizeEnabled(enabled);
    this.transformer.enabledAnchors(
      enabled
        ? [
            'top-left',
            'top-center',
            'top-right',
            'middle-right',
            'middle-left',
            'bottom-left',
            'bottom-center',
            'bottom-right',
          ]
        : []
    );
  }

  /**
   * Set keep aspect ratio
   */
  setKeepAspectRatio(keep: boolean): void {
    this.options.keepAspectRatio = keep;
    this.transformer.keepRatio(keep);
  }

  /**
   * Get the transformer instance
   */
  getTransformer(): Konva.Transformer {
    return this.transformer;
  }

  /**
   * Update rotation trigger positions (public method for external updates during drag, etc.)
   */
  updateRotationTriggers(): void {
    this.updateRotationTriggersPosition();
  }

  /**
   * Force update (when nodes are added/removed)
   */
  refresh(): void {
    this.updateTransformer();
    // Update rotation triggers with a small delay to ensure transformer is updated
    setTimeout(() => {
      this.updateRotationTriggersPosition();
    }, 0);
  }

  /**
   * Destroy the selection manager
   */
  destroy(): void {
    this.transformer.destroy();
    if (this.marqueeRect) {
      this.marqueeRect.destroy();
    }
    // Destroy rotation triggers
    this.rotationTriggers.forEach(t => t.destroy());
    this.rotationTriggers = [];
    
    this.selectedIds = [];
    this.onTransformStart = null;
    this.onTransformEnd = null;
    this.isRotating = false;
    this.rotatingNode = null;
  }
}
